import React, { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import Modal from '../../../../../components/shared/Modal/Modal';
import { PurchaseOrder } from '../../../../../types/PurchaseOrder.types';
import { formatQuantity, parseNumber } from './format';

export interface ReceiptHeaderInput {
  deliveryNoteNumber: string;
  packageCount: number | null;
  notes: string;
}

export interface ReceiptLineInput {
  purchaseOrderLineId: string;
  quantityReceived: number;
}

interface GoodsReceiptModalProps {
  order: PurchaseOrder | null; // null = cerrado
  isSaving: boolean;
  error: string;
  onConfirm: (header: ReceiptHeaderInput, lines: ReceiptLineInput[]) => void;
  onCancel: () => void;
}

const pendingOf = (line: { quantityOrdered: number; quantityReceived: number }) =>
  Math.max(line.quantityOrdered - line.quantityReceived, 0);

// Registro de un albarán contra la OC seleccionada. El almacén es el de la orden
// y no se puede cambiar: el backend lo exige físico y es donde el trigger de
// stock deja la mercancía. Cada línea admite como máximo lo pendiente; el
// backend rechaza igualmente cualquier exceso.
export const GoodsReceiptModal = ({ order, isSaving, error, onConfirm, onCancel }: GoodsReceiptModalProps) => {
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState('');
  const [packageCount, setPackageCount] = useState('');
  const [notes, setNotes] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [localError, setLocalError] = useState('');

  // Se limpia al abrir con otra orden (el componente queda montado entre aperturas).
  useEffect(() => {
    setDeliveryNoteNumber('');
    setPackageCount('');
    setNotes('');
    setQuantities({});
    setLocalError('');
  }, [order && order.id]);

  if (!order) return null;

  const receivableLines = order.lines.filter(l => pendingOf(l) > 0);

  const setQuantity = (lineId: string, value: string) => {
    setQuantities(prev => ({ ...prev, [lineId]: value }));
    if (localError) setLocalError('');
  };

  const receiveAllPending = () => {
    const all: Record<string, string> = {};
    receivableLines.forEach(l => { all[l.id] = String(pendingOf(l)); });
    setQuantities(all);
  };

  const handleConfirm = () => {
    if (isSaving) return;

    const lines: ReceiptLineInput[] = [];
    for (const l of receivableLines) {
      const qty = parseNumber(quantities[l.id] || '0');
      if (qty < 0) { setLocalError(`"${l.description}": la cantidad no puede ser negativa.`); return; }
      if (qty > pendingOf(l)) { setLocalError(`"${l.description}": no se pueden recibir más de ${formatQuantity(pendingOf(l))} unidades pendientes.`); return; }
      if (qty > 0) lines.push({ purchaseOrderLineId: l.id, quantityReceived: qty });
    }
    if (lines.length === 0) { setLocalError('Indique la cantidad recibida de al menos una línea.'); return; }

    onConfirm(
      {
        deliveryNoteNumber: deliveryNoteNumber.trim(),
        packageCount: packageCount.trim() === '' ? null : Math.max(0, Math.round(parseNumber(packageCount))),
        notes: notes.trim()
      },
      lines
    );
  };

  const message = error || localError;

  return (
    <Modal
      isOpen={!!order}
      type="info"
      title={`Registrar recepción · ${order.poNumber}`}
      message={`Entrada en ${order.warehouseName || `almacén #${order.warehouseId}`} de mercancía de ${order.supplierName}.`}
      confirmText={isSaving ? 'Registrando...' : 'Registrar recepción'}
      cancelText="Cancelar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className="module-space-y-4 purchases-view__receipt-modal">
        {message && (
          <div className="module-form-error">
            <AlertCircle size={14} style={{ flexShrink: 0 }} />
            <span>{message}</span>
          </div>
        )}

        <div className="module-form-grid-3">
          <div className="module-form-group">
            <label className="module-form-label">Almacén de recepción</label>
            <input type="text" value={order.warehouseName || `#${order.warehouseId}`} readOnly disabled className="module-form-input" />
          </div>
          <div className="module-form-group">
            <label className="module-form-label">Nº de albarán del acreedor</label>
            <input
              type="text"
              value={deliveryNoteNumber}
              onChange={(e) => setDeliveryNoteNumber(e.target.value)}
              disabled={isSaving}
              placeholder="ALB-2026-0001"
              className="module-form-input"
              autoFocus
            />
          </div>
          <div className="module-form-group">
            <label className="module-form-label">Bultos</label>
            <input
              type="number"
              min="0"
              step="1"
              value={packageCount}
              onChange={(e) => setPackageCount(e.target.value)}
              disabled={isSaving}
              className="module-form-input"
            />
          </div>
        </div>

        <div className="purchases-view__lines-wrapper">
          <table className="purchases-view__lines-table purchases-view__lines-table--readonly">
            <thead>
              <tr>
                <th>Artículo</th>
                <th className="right">Pedida</th>
                <th className="right">Ya recibida</th>
                <th className="right">Pendiente</th>
                <th className="right">Recibir ahora</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map(l => {
                const pending = pendingOf(l);
                return (
                  <tr key={l.id} className={pending === 0 ? 'purchases-view__line-done' : ''}>
                    <td>
                      <div className="purchases-view__line-desc">{l.description}</div>
                      {l.sku && <div className="purchases-view__line-sub">{l.sku}</div>}
                    </td>
                    <td className="right">{formatQuantity(l.quantityOrdered)} {l.unitOfMeasure}</td>
                    <td className="right">{formatQuantity(l.quantityReceived)}</td>
                    <td className="right">{formatQuantity(pending)}</td>
                    <td className="right purchases-view__receive-cell">
                      {pending === 0 ? (
                        <span className="module-badge completed">Completa</span>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          max={pending}
                          step="1"
                          value={quantities[l.id] || ''}
                          onChange={(e) => setQuantity(l.id, e.target.value)}
                          disabled={isSaving}
                          placeholder="0"
                          className="module-form-input right"
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="purchases-view__lines-footer">
          <button type="button" onClick={receiveAllPending} disabled={isSaving || receivableLines.length === 0} className="module-form-cancel-btn">
            Recibir todo lo pendiente
          </button>
          <span className="purchases-view__actions-hint">
            Si queda algo pendiente, la orden pasará a "Recibida parcialmente".
          </span>
        </div>

        <div className="module-form-group">
          <label className="module-form-label">Notas de la recepción</label>
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSaving}
            placeholder="Incidencias, embalaje dañado, faltas..."
            className="module-form-textarea"
          />
        </div>
      </div>
    </Modal>
  );
};
